<script setup lang="ts">
// ================================
// IMPORTS
// ================================
import { computed, ref, watch } from "vue"
import { useI18n } from "vue-i18n"
import { coreServiceRegistry } from "@core/services/register"
import { formatDate, formatIdSource, parseDate } from "@core/helpers"
import type { CompanyType } from "@core/composables"

// ================================
// TYPES
// ================================

type SummaryOwner = {
    user: {
        firstname?: string
        lastname?: string
        username?: string
    }
    company?: { name?: string; type?: string } | null
}

// ================================
// PROPS & EMITS
// ================================

const props = defineProps<{
    consentRequestId: string
    idsource: string
    initialZipcode: string
    initialDate: string
    owner: SummaryOwner
}>()

const emit = defineEmits<{
    "update:request": [payload: { date: string; zipcode: string }]
    done: []
    "click:quit": []
}>()

// ================================
// I18N
// ================================

const { t } = useI18n({
    messages: {
        fr: {
            title: "Validation de Déclaration de Droit d'Accès ( DDA )",
            message:
                "Pour mettre à jour l'accès aux données du compteur de gaz, commencez par examiner attentivement les informations actuelles. Si vous constatez que le code postal a changé ou que la date d'ouverture du contrat avec le fournisseur nécessite une correction, veuillez effectuer les modifications nécessaires.",
            message2:
                "Cette démarche assure que les données du compteur sont toujours associées aux informations les plus récentes et précises vous concernant, facilitant ainsi la validation de la déclaration d'accès aux données par GRDF.",
            field_name_pro: "Représentant",
            field_name_part: "Titulaire",
            field_username: "Adresse email",
            field_idsource: "Numéro du Point de Comptage et d'Estimation (PCE)",
            zipcode_label: "Code postal du bâtiment",
            zipcode_placeholder: "XXXXX",
            zipcode_error: "Le format du code postal est incorrect.",
            date_label: "Date d'ouverture du contrat avec le fournisseur",
            company_entreprise: "Entreprise",
            company_asso: "Association",
            company_copro: "Copropriété",
            company_collectivite: "Collectivité",
            info: "Attention à ne pas renseigner une date de début de contrat antérieure à la date de votre installation dans les locaux.",
            submit: "Valider",
            quit: "Retour",
        },
    },
})

// ================================
// STATE
// ================================

const regexZipcode = /^(?:0[1-9]|[1-8]\d|9[0-8])\d{3}$/

/**
 * Ramène une date renvoyée par l’API (`YYYY-MM-DD` ou ISO instant) vers `YYYY-MM-DD`
 * pour le state et `parseDate`, qui ne gère pas les suffixes `T00:00:00.000Z`.
 */
function normalizeToDateOnly(raw: string): string {
    const trimmed = raw?.trim() ?? ""
    if (!trimmed) return ""
    if (/^\d{4}-\d{2}-\d{2}$/.test(trimmed)) return trimmed
    const d = new Date(trimmed)
    return Number.isNaN(d.getTime()) ? "" : formatDate(d, "YYYY-MM-DD")
}

const zipcode = ref(props.initialZipcode)
const dateStr = ref(normalizeToDateOnly(props.initialDate))
const isSubmitting = ref(false)
const errorMessage = ref<string | null>(null)

watch(
    () => props.initialZipcode,
    (v) => {
        zipcode.value = v
    }
)
watch(
    () => props.initialDate,
    (v) => {
        dateStr.value = normalizeToDateOnly(v)
    }
)

// ================================
// COMPUTED
// ================================

const getCompanyFieldLabel = computed(() => {
    const ty = props.owner?.company?.type as CompanyType | undefined
    if (ty === "asso") return t("company_asso")
    if (ty === "copro") return t("company_copro")
    if (ty === "collectivite") return t("company_collectivite")
    return t("company_entreprise")
})

const summaryLines = computed(() => {
    const lines: { label: string; value: string }[] = []
    if (props.owner?.company?.name) {
        lines.push({
            label: getCompanyFieldLabel.value,
            value: props.owner.company.name,
        })
    }
    lines.push(
        {
            label: props.owner?.company?.name
                ? t("field_name_pro")
                : t("field_name_part"),
            value: `${props.owner?.user?.firstname ?? ""} ${props.owner?.user?.lastname ?? ""}`.trim(),
        },
        {
            label: t("field_username"),
            value: props.owner?.user?.username ?? "",
        },
        {
            label: t("field_idsource"),
            value: formatIdSource(props.idsource, "pce"),
        }
    )
    return lines
})

const zipcodeInvalid = computed(
    () => zipcode.value.length > 0 && !regexZipcode.test(zipcode.value)
)

// ================================
// ACTIONS
// ================================

/**
 * Met à jour la date stockée au format YYYY-MM-DD à partir du DatePicker.
 * On utilise formatDate (Europe/Paris) plutôt que toISOString(), qui exprime
 * la date en UTC et peut décaler d’un jour selon le fuseau.
 */
function onDateChange(value: Date | string | null): void {
    if (value == null) {
        dateStr.value = ""
        return
    }
    const d = typeof value === "string" ? new Date(value) : value
    if (Number.isNaN(d.getTime())) {
        dateStr.value = ""
        return
    }
    dateStr.value = formatDate(d, "YYYY-MM-DD")
}

async function onSubmit(): Promise<void> {
    if (!dateStr.value || !zipcode.value || zipcodeInvalid.value) return
    errorMessage.value = null
    isSubmitting.value = true
    try {
        if (
            dateStr.value !== normalizeToDateOnly(props.initialDate) ||
            zipcode.value !== props.initialZipcode
        ) {
            await coreServiceRegistry.consentRequest.updateConsentRequestPCE({
                requestId: props.consentRequestId,
                date: dateStr.value,
                zipcode: zipcode.value,
            })
            emit("update:request", {
                date: dateStr.value,
                zipcode: zipcode.value,
            })
        }
        emit("done")
    } catch (e) {
        errorMessage.value = (e as Error)?.message ?? "Une erreur est survenue."
    } finally {
        isSubmitting.value = false
    }
}
</script>

<template>
    <div class="flex flex-col items-center gap-6 w-full max-w-lg mx-auto">
        <header class="flex flex-col gap-2 text-center w-full">
            <h2 class="text-lg font-semibold text-gray-800">
                {{ t("title") }}
            </h2>
            <p class="text-sm text-gray-600 text-left">
                {{ t("message") }}
            </p>
            <p class="text-sm text-gray-600 text-left">
                {{ t("message2") }}
            </p>
        </header>

        <div
            class="w-full flex flex-col gap-3 rounded-lg border border-gray-200 p-4 bg-white"
        >
            <div
                v-for="(row, i) in summaryLines"
                :key="i"
                class="flex flex-col sm:flex-row sm:gap-2 text-sm"
            >
                <span class="font-medium text-gray-600 shrink-0">{{
                    row.label
                }}</span>
                <span class="text-gray-900">{{ row.value }}</span>
            </div>
            <BaseInputWrapper
                unfloat
                input-id="pce-inv-zip"
                :label="t('zipcode_label')"
                :error="zipcodeInvalid ? t('zipcode_error') : undefined"
            >
                <BaseInputText
                    id="pce-inv-zip"
                    v-model="zipcode"
                    :placeholder="t('zipcode_placeholder')"
                    class="w-full"
                    maxlength="5"
                />
            </BaseInputWrapper>
            <BaseInputWrapper
                unfloat
                input-id="pce-inv-date"
                :label="t('date_label')"
            >
                <BaseDatePicker
                    :model-value="parseDate(dateStr, 'YYYY-MM-DD')"
                    date-format="dd/mm/yy"
                    show-icon
                    :max-date="new Date()"
                    icon-display="input"
                    input-id="pce-inv-date"
                    @update:model-value="onDateChange"
                />
            </BaseInputWrapper>
        </div>

        <BaseMessage severity="info" class="w-full" :closable="false">
            {{ t("info") }}
        </BaseMessage>

        <BaseMessage
            v-if="errorMessage"
            severity="error"
            class="w-full"
            :closable="false"
        >
            {{ errorMessage }}
        </BaseMessage>

        <div class="flex flex-wrap justify-between gap-3 w-full">
            <BaseButton
                :label="t('quit')"
                severity="secondary"
                class="min-w-[10rem]"
                :disabled="isSubmitting"
                @click="emit('click:quit')"
            />
            <BaseButton
                :label="t('submit')"
                class="min-w-[10rem]"
                :loading="isSubmitting"
                :disabled="
                    !dateStr || !zipcode || zipcodeInvalid || isSubmitting
                "
                @click="onSubmit"
            />
        </div>
    </div>
</template>
